import React, { useCallback, useEffect } from 'react';
import { styled } from '@compiled/react';
import { useCrossFlow, Journeys, Targets } from '@atlassiansox/cross-flow-support';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Button, { ButtonGroup } from '@atlaskit/button';
import Heading from '@atlaskit/heading';
import { CompassLogo } from '@atlaskit/logo';
import { N500 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/index.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { FireUiAnalytics } from '@atlassian/jira-product-analytics-bridge';
import {
	fireTrackAnalytics,
	fireUIAnalytics,
} from '@atlassian/jira-product-analytics-bridge/src/utils/fire-analytics';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import { useFireCrossFlowAnalyticsEvents } from '@atlassian/surface-analytics';
import { CrossflowImage } from '../assets/index.tsx';
import { COMPASS_PRODUCT_LINK } from '../common/constants.tsx';
import { hasCompassBeenProvisioned } from '../services/license-information/index.tsx';
import messages from './messages.tsx';
import type { Props, TouchPointProps } from './types.tsx';

const NewCrossFlowTouchPoint = (props: TouchPointProps) => {
	const { formatMessage } = useIntl();
	const { enabled, onTry, onLearnMore } = props;
	if (enabled) {
		return (
			<ContentWrapper data-testid="compass-cross-flow.ui.empty-state-not-provisioned">
				<div>
					<CompassLogo appearance="brand" size="medium" />
					<TouchPointMessageWrapper>
						<Heading size="large" as="h2">
							{formatMessage(messages.headLine)}
						</Heading>
					</TouchPointMessageWrapper>
					<TouchPointMessageDescriptionWrapper>
						{formatMessage(messages.callToAction)}
					</TouchPointMessageDescriptionWrapper>
					<ButtonGroup>
						<Button appearance="primary" onClick={onTry}>
							{formatMessage(messages.engageMessage)}
						</Button>
						<Button
							appearance="link"
							onClick={onLearnMore}
							href={COMPASS_PRODUCT_LINK}
							target="_blank"
						>
							{formatMessage(messages.learnMoreMessage)}
						</Button>
					</ButtonGroup>
				</div>
				<CrossflowImage />
			</ContentWrapper>
		);
	}
	return null;
};

const CrossFlowTouchPoint = (props: Props) => {
	const { origin, eventSource, onCrossFlowComplete } = props;
	const crossFlow = useCrossFlow();
	const { cloudId } = useTenantContext();
	const { fireInteractedUIEvent, fireViewedUIEvent } = useFireCrossFlowAnalyticsEvents();

	useEffect(() => {
		if (!crossFlow.isEnabled) {
			fireErrorAnalytics({
				error: new Error(crossFlow.reason),
				meta: {
					id: crossFlow.reason,
					packageName: 'jiraCompassCrossFlow',
				},
			});
		} else {
			fireViewedUIEvent(
				{
					action: 'viewed',
					actionSubject: 'section',
					actionSubjectId: 'tryCompassBanner',
				},
				{
					recommendedProductIds: [Targets.COMPASS],
					source: eventSource,
				},
			);
		}
	}, [crossFlow, eventSource, fireViewedUIEvent]);

	const onTry = useCallback(
		async (event: React.MouseEvent<HTMLElement>, analyticsEvent: UIAnalyticsEvent) => {
			fireInteractedUIEvent(
				{
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'tryCompassButton',
				},
				{
					targetProductId: Targets.COMPASS,
					recommendedProductIds: [Targets.COMPASS],
					source: 'tryCompassBannerSection',
				},
			);
			fireUIAnalytics(analyticsEvent, 'compassCrossFlowTouchPointTry');
			if (crossFlow.isEnabled) {
				await crossFlow.api.open({
					journey: Journeys.GET_STARTED,
					targetProduct: Targets.COMPASS,
					sourceComponent: 'jira-compass-cross-flow-try',
					sourceContext: 'jira',
				});

				// Refresh the Compass Crossflow Touchpoint once Compass is provisioned
				const result = await hasCompassBeenProvisioned(cloudId);
				if (result) {
					fireTrackAnalytics(analyticsEvent, 'compassCrossflow expandComplete', {
						hasCompassProvisioned: result,
						origin,
					});
					onCrossFlowComplete?.();
				} else {
					fireTrackAnalytics(analyticsEvent, 'compassCrossflow expandIncomplete', {
						hasCompassProvisioned: result,
						origin,
					});
				}
			}
		},
		[cloudId, crossFlow, origin, fireInteractedUIEvent, onCrossFlowComplete],
	);

	const onLearnMore = useCallback(
		async (event: React.MouseEvent<HTMLElement>, analyticsEvent: UIAnalyticsEvent) => {
			fireInteractedUIEvent(
				{
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'learnMoreCompassButton',
				},
				{
					targetProductId: Targets.COMPASS,
					recommendedProductIds: [Targets.COMPASS],
					source: 'tryCompassBannerSection',
				},
			);
			fireUIAnalytics(analyticsEvent, 'compassCrossFlowTouchPointLearnMore');
		},
		[fireInteractedUIEvent],
	);

	return (
		<JSErrorBoundary
			teamName="compass-lodestone"
			id="crossFlowTouchPoint"
			packageName="jiraCompassCrossFlowTouchPoint"
			sendToPrivacyUnsafeSplunk
		>
			<FireUiAnalytics action="viewed" actionSubject="jiraCompassCrossFlowTouchPoint" />
			{NewCrossFlowTouchPoint({
				enabled: crossFlow.isEnabled,
				onTry,
				onLearnMore,
			})}
		</JSErrorBoundary>
	);
};

export default CrossFlowTouchPoint;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ContentWrapper = styled.div({
	display: 'flex',
	flexDirection: 'row',
	justifyContent: 'space-between',
	width: '920px',
	color: token('color.text.subtle', N500),
	marginTop: token('space.1000', '100px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TouchPointMessageWrapper = styled.div({
	fontWeight: 500,
	paddingTop: token('space.250', '20px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TouchPointMessageDescriptionWrapper = styled.div({
	font: token('font.body'),
	padding: `${token('space.250', '20px')} 0px`,
});
